import { useState, useEffect, useCallback, useRef } from 'react';
import { queueAPI } from '../services/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const useQueueMonitor = (pollInterval = 30000) => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connected, setConnected] = useState(false);
  const prevCountRef = useRef(null); // null = initial load, skip sound
  const eventSourceRef = useRef(null);

  const fetchQueue = useCallback(async () => {
    try {
      const response = await queueAPI.getQueue();
      const newQueue = response.data.queue || [];

      // Play sound only if count increased after initial load
      if (prevCountRef.current !== null && newQueue.length > prevCountRef.current) {
        playNotificationSound();
      }
      prevCountRef.current = newQueue.length;

      setQueue(newQueue);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to fetch queue');
    } finally {
      setLoading(false);
    }
  }, []);

  // SSE connection
  useEffect(() => {
    const secretKey = localStorage.getItem('secretKey') || localStorage.getItem('terminalSecretKey');
    if (!secretKey) return;

    // Initial fetch
    fetchQueue();

    // Open SSE stream — pass auth via query param since EventSource doesn't support headers
    const streamUrl = `${API_BASE_URL}/terminal/queue/stream?token=${encodeURIComponent(secretKey)}`;
    const es = new EventSource(streamUrl);
    eventSourceRef.current = es;

    es.addEventListener('connected', () => {
      setConnected(true);
      setError(null);
    });

    es.addEventListener('item_created', () => {
      fetchQueue();
    });

    es.addEventListener('item_status_changed', () => {
      fetchQueue();
    });

    es.onerror = () => {
      setConnected(false);
      // EventSource auto-reconnects; no action needed
    };

    // Fallback polling at a slower rate while SSE is active
    const interval = setInterval(fetchQueue, pollInterval);

    return () => {
      es.close();
      eventSourceRef.current = null;
      setConnected(false);
      clearInterval(interval);
    };
  }, [fetchQueue, pollInterval]);

  const playNotificationSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const refresh = () => {
    setLoading(true);
    fetchQueue();
  };

  return { queue, loading, error, lastUpdate, refresh, connected };
};
